import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { ArrowRight, ArrowUpRight, ArrowDownRight, CheckCircle2, Clock, Eye, TrendingUp, Zap, Globe2, ClipboardList, Palette, X, Info, ChevronDown, Download, Upload } from "lucide-react";

const FEATURES = [
  { icon: Clock, title: "1-minute setup", body: "Paste keywords, pick a country, hit track. First rankings appear in under 60 seconds." },
  { icon: Eye, title: "Transparent updates", body: "We show when data was last refreshed and warn you when it's delayed. No hiding." },
  { icon: ClipboardList, title: "SEO Activity Timeline", body: "Log every backlink, on-page edit, and content refresh in one place — then see exactly which actions moved your rankings." },
  { icon: TrendingUp, title: "Executive summary", body: "One card, every morning: improved, dropped, top 3, top 10, click impact." },
  { icon: Globe2, title: "Multi-country", body: "Track the same keyword across US, UK, DE, IN, AU — mobile and desktop, side by side." },
  { icon: Palette, title: "White-label reports", body: "Send branded PDF reports and share links on your own domain. Clients see your brand, not ours." },
];

const AUDIENCES = [
  "SEO agencies",
  "Freelancers & consultants",
  "In-house marketers",
  "Small business owners",
  "Bloggers & content creators",
  "Startups",
];

export default function Home() {
  return (
    <>
      <SEO pageKey="home" title="SerpNex — Simple, Transparent Keyword Rank Tracking" description="Simple enough for solo bloggers. Strong enough for agencies. Track keyword rankings on Google and Bing with a clear morning summary and white-label reports. Try free for 7 days — no credit card." canonical="/" />

      <section className="relative overflow-hidden hero-grid hero-noise">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 md:pt-28 md:pb-32 relative">
          <div className="grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <span className="pill pill-accent" data-testid="hero-badge">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Daily rank tracking · Google &amp; Bing
              </span>
              <h1 className="mt-6 h-display text-5xl sm:text-6xl md:text-7xl font-bold text-[#0A0D14]">
                Every ranking change.<br />
                <span className="text-[#0047FF]">Explained.</span>
              </h1>
              <p className="mt-6 text-[17px] leading-relaxed text-[#525B6A] max-w-xl">
                Simple enough for a solo blogger. Strong enough for a full agency. Track your keywords, understand what moved — and why — in under two minutes.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/signup" data-testid="hero-cta-primary" className="btn-primary px-5 py-3 rounded-md text-sm inline-flex items-center gap-2">
                  Start 7-Day Free Trial <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/tools/rank-checker" data-testid="hero-cta-secondary" className="btn-ghost px-5 py-3 rounded-md text-sm">
                  Free rank checker →
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[#525B6A]">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> 7-day free trial</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> No credit card</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Cancel anytime</span>
              </div>
            </div>

            <div className="md:col-span-5">
              <MockDashboardCard />
            </div>
          </div>
        </div>
      </section>

      {/* Audience bar — anti-positioning statement gets top billing here.
          Two-tone: the qualifier is muted; the differentiator ("without
          paying for an entire SEO suite") lands as the emotional hook so
          landing visitors register the value prop in <1s. */}
      <section className="border-y border-[#E2E8F0] bg-gradient-to-b from-white to-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#0047FF] font-semibold mb-4 px-2.5 py-1 rounded-full bg-[#0047FF]/[0.08] border border-[#0047FF]/15" data-testid="audience-eyebrow">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#0047FF]" />Who this is for
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0A0D14] leading-tight tracking-tight" data-testid="audience-tagline">
            Built for anyone who needs simple keyword tracking —{" "}
            <span className="relative inline-block whitespace-nowrap">
              <span className="relative z-10 text-[#0047FF]">without paying for an entire SEO suite</span>
              <span aria-hidden="true" className="absolute left-0 right-0 bottom-1 h-2 sm:h-2.5 bg-[#0047FF]/[0.14] rounded-sm -z-0" />
            </span>
            .
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2.5">
            {AUDIENCES.map((a, i) => (
              <span key={i} data-testid={`audience-${i}`} className="text-sm text-[#525B6A] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#E2E8F0] shadow-sm">
                <CheckCircle2 className="h-3.5 w-3.5 text-[#0047FF]" />{a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <Stat label="Time to first ranking" value="< 60s" />
          <Stat label="Countries supported" value="120+" />
          <Stat label="Update cadence" value="Daily" />
          <Stat label="Free trial" value="7 days" />
        </div>
      </section>

      {/* SEO Activity Timeline */}
      <section className="border-t border-[#E2E8F0] bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eyebrow">SEO Activity Timeline</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold leading-snug text-[#0A0D14]">
              Log every SEO action. <br />See what actually moved the needle.
            </h2>
            <p className="mt-5 text-[#525B6A] leading-relaxed max-w-lg">
              Capture backlinks, guest posts, on-page tweaks, and content refreshes in one clean timeline — then watch how each activity nudges rankings over the next 7, 14, and 30 days. No more guessing which action worked.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-[#0A0D14]">
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Backlinks · guest posts · on-page edits · content refreshes</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Link each activity to the exact keyword or page it targets</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Impact tracked automatically over 7 / 14 / 30 / 60 days</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Prove work to clients with a clean, dated audit trail</li>
            </ul>
            <Link to="/signup" data-testid="activity-timeline-cta" className="mt-8 inline-flex btn-ghost px-4 py-2.5 rounded-md text-sm items-center gap-2">Start tracking activities <ArrowRight className="h-4 w-4" /></Link>
            <div className="mt-6 flex items-start gap-2 max-w-lg text-[11px] leading-relaxed text-[#525B6A] border-l-2 border-[#E2E8F0] pl-3" data-testid="activity-timeline-disclaimer">
              <Info className="h-3.5 w-3.5 text-[#8A94A6] shrink-0 mt-0.5" />
              <span>Ranking changes after a guest post or backlink may be influenced by multiple SEO factors. SerpNex shows observed movement after this activity date — treat this as correlation, not guaranteed causation.</span>
            </div>
          </div>
          {/* Mockup collage on the RIGHT — ranking history chart + Add Activity peek */}
          <div className="relative" data-testid="mock-activity-timeline">
            {/* Base: Ranking history with activity annotations */}
            <div className="card-crisp p-5 bg-white" data-testid="mock-ranking-history">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#8A94A6] font-medium">Keyword</div>
                  <div className="mt-1 text-xl font-display font-bold text-[#0A0D14]">solar panel US</div>
                  <div className="mt-0.5 text-[11px] text-[#525B6A]">US · desktop · google</div>
                </div>
                <X className="h-4 w-4 text-[#8A94A6] shrink-0" />
              </div>
              {/* KPI cards */}
              <div className="mt-4 border-t border-[#E2E8F0] pt-4 grid grid-cols-3 gap-2">
                <div className="border border-[#E2E8F0] rounded-md p-2.5">
                  <div className="text-[9px] uppercase tracking-widest text-[#8A94A6] font-medium">Current</div>
                  <div className="stat-value text-2xl font-bold text-[#0047FF]">#76</div>
                </div>
                <div className="border border-[#E2E8F0] rounded-md p-2.5">
                  <div className="text-[9px] uppercase tracking-widest text-[#8A94A6] font-medium">Previous</div>
                  <div className="stat-value text-2xl font-bold text-[#0A0D14]">#74</div>
                </div>
                <div className="border border-[#E2E8F0] rounded-md p-2.5">
                  <div className="text-[9px] uppercase tracking-widest text-[#8A94A6] font-medium">Best</div>
                  <div className="stat-value text-2xl font-bold text-emerald-600">#74</div>
                </div>
              </div>
              {/* Chart header + legend */}
              <div className="mt-4 flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-[#8A94A6] font-medium">Ranking history (30 days)</div>
                <div className="flex items-center gap-3 text-[9px] uppercase tracking-wider text-[#525B6A] font-medium">
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-600"></span> Backlinks</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#93BFFF]"></span> Content</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-orange-500"></span> Technical</span>
                </div>
              </div>
              {/* Chart */}
              <div className="mt-2 border border-[#E2E8F0] rounded-md p-3 relative">
                <svg viewBox="0 0 500 200" className="w-full h-40 overflow-visible">
                  {/* horizontal grid lines */}
                  {[15, 63, 111, 159, 195].map((y, i) => (
                    <line key={i} x1="30" x2="490" y1={y} y2={y} stroke="#E2E8F0" strokeWidth="0.5" />
                  ))}
                  {/* Y-axis labels */}
                  <text x="18" y="19" fontSize="8" fill="#8A94A6" textAnchor="end">72</text>
                  <text x="18" y="67" fontSize="8" fill="#8A94A6" textAnchor="end">76</text>
                  <text x="18" y="115" fontSize="8" fill="#8A94A6" textAnchor="end">80</text>
                  <text x="18" y="163" fontSize="8" fill="#8A94A6" textAnchor="end">85</text>
                  {/* Rank line */}
                  <polyline
                    fill="none"
                    stroke="#0047FF"
                    strokeWidth="2"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    points="40,147 69,135 98,159 127,147 156,111 185,99 214,99 243,111 272,99 301,99 330,87 359,99 388,87 417,75 446,75 475,63"
                  />
                  {/* Data point circles (open) */}
                  {[[40,147],[69,135],[98,159],[127,147],[156,111],[185,99],[214,99],[272,99],[301,99],[330,87],[359,99],[388,87],[417,75],[446,75]].map(([x,y],i)=>(
                    <circle key={i} cx={x} cy={y} r="2.5" fill="#fff" stroke="#0047FF" strokeWidth="1.2" />
                  ))}
                  {/* Activity marker: TECHNICAL @ 4 Aug */}
                  <circle cx="243" cy="111" r="5" fill="#f97316" stroke="#fff" strokeWidth="2" />
                  {/* Activity marker: BACKLINKS @ 24 Aug */}
                  <circle cx="475" cy="63" r="6" fill="#10b981" stroke="#fff" strokeWidth="2" />
                </svg>
                {/* X-axis labels */}
                <div className="flex justify-between px-1 pt-1 text-[9px] text-[#8A94A6] font-medium">
                  <span>27 Jul</span><span>31 Jul</span><span>4 Aug</span><span>8 Aug</span><span>12 Aug</span><span>16 Aug</span><span>20 Aug</span><span>24 Aug</span>
                </div>
                {/* Tooltip callout pointing at 24 Aug node */}
                <div className="absolute top-2 right-3 w-52 rounded-md border border-[#E2E8F0] bg-white shadow-lg p-3">
                  <div className="text-[11px] font-semibold text-[#0A0D14]">24 Aug 2026</div>
                  <div className="text-[10px] text-[#525B6A] mt-0.5">Rank: <span className="font-medium text-[#0A0D14]">#76</span></div>
                  <div className="mt-2 pt-2 border-t border-[#E2E8F0]">
                    <div className="text-[9px] uppercase tracking-wider text-[#8A94A6] font-medium">SEO Activity</div>
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-emerald-600 shrink-0"></span>
                      <span className="text-[11px] font-medium text-[#0A0D14]">Top 10 Solar Panel USA</span>
                    </div>
                    <div className="text-[9px] uppercase tracking-wider text-[#8A94A6] mt-0.5 font-medium">Backlinks · Guest Post Published</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Overlay: compact Add SEO Activity peek */}
            <div className="hidden lg:block absolute -bottom-6 -left-6 w-64 -rotate-2 shadow-xl">
              <div className="card-crisp p-4 bg-white">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-display font-bold text-[#0A0D14]">Add SEO Activity</div>
                  <X className="h-3.5 w-3.5 text-[#8A94A6]" />
                </div>
                <div className="mt-3 border-t border-[#E2E8F0] pt-3 space-y-2.5">
                  <div>
                    <div className="text-[10px] font-semibold text-[#0A0D14] mb-1">Activity Type <span className="text-red-500">*</span></div>
                    <div className="flex items-center justify-between px-2.5 py-1.5 border-2 border-[#0A0D14] rounded-md text-[11px] text-[#0A0D14]">
                      Guest Post Published
                      <ChevronDown className="h-3 w-3 text-[#525B6A]" />
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-semibold text-[#0A0D14] mb-1">Title <span className="text-red-500">*</span></div>
                    <div className="px-2.5 py-1.5 border border-[#E2E8F0] rounded-md text-[10px] text-[#8A94A6] truncate">Top 10 Solar Panel USA</div>
                  </div>
                </div>
                <button className="mt-3 w-full py-1.5 rounded-md bg-[#0047FF] text-white text-[11px] font-medium">Save Activity</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Easy Setup — Add + Import */}
      <section className="border-t border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid md:grid-cols-2 gap-10 items-center">
          {/* Description on the LEFT */}
          <div>
            <div className="eyebrow">Easy Setup</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold leading-snug text-[#0A0D14]">
              Add Fresh Keywords &amp; <br />Import Ranking History.
            </h2>
            <p className="mt-5 text-[#525B6A] leading-relaxed max-w-lg">
              Bulk-paste new keywords or upload a CSV / XLSX export from another tool.
            </p>
            <p className="mt-3 text-[#525B6A] leading-relaxed max-w-lg">
              Switching from Ahrefs, Semrush, or AccuRanker? Bring your existing rank history along — SerpNex picks up your chart right where the old tool left off, so you never leave a single day of tracking behind.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-[#0A0D14]">
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Paste hundreds of keywords in one go — no per-row forms</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Upload CSV or XLSX from Ahrefs / Semrush / AccuRanker</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Migrate rank history — never lose a data point on switching</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Set default country, language, device &amp; engine per project</li>
            </ul>
            <Link to="/signup" data-testid="easy-setup-cta" className="mt-8 inline-flex btn-ghost px-4 py-2.5 rounded-md text-sm items-center gap-2">See setup in action <ArrowRight className="h-4 w-4" /></Link>
          </div>

          {/* Mockup collage on the RIGHT */}
          <div className="relative" data-testid="mock-easy-setup">
            {/* Base: Import Keywords modal */}
            <div className="card-crisp p-5">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <div className="text-lg font-display font-bold text-[#0A0D14]">Import keywords</div>
                  <div className="text-[11px] text-[#8A94A6] mt-0.5">Pick a template, download it, fill it in, upload it.</div>
                </div>
                <X className="h-4 w-4 text-[#8A94A6] shrink-0" />
              </div>
              <div className="mt-4 border-t border-[#E2E8F0] pt-4 grid grid-cols-2 gap-2">
                <div className="p-3 rounded-md border border-[#E2E8F0]">
                  <div className="text-[11px] font-semibold text-[#0A0D14]">Standard</div>
                  <div className="text-[10px] text-[#525B6A] mt-0.5">Add fresh keywords</div>
                </div>
                <div className="p-3 rounded-md border-2 border-[#0047FF] bg-[#0047FF]/[0.04]">
                  <div className="text-[11px] font-semibold text-[#0047FF]">Migrate from another tool</div>
                  <div className="text-[10px] text-[#525B6A] mt-0.5">Bring your rank history</div>
                </div>
              </div>
              <div className="mt-3 rounded-md border border-[#E2E8F0] bg-[#F8F9FA] p-3 flex items-start justify-between gap-3">
                <div className="text-[11px] text-[#525B6A] min-w-0">
                  <div className="font-semibold text-[#0A0D14]">Expected columns</div>
                  <div className="font-mono text-[10px] mt-0.5 break-all">keyword, country, language, device, engine, tags, year-month-date:position</div>
                  <div className="mt-1 leading-relaxed">The final column carries your existing rank history as pipe-separated pairs — e.g. 2026-01-15:8.</div>
                </div>
                <div className="shrink-0 flex flex-col gap-1">
                  <button className="border border-[#E2E8F0] rounded-md px-2 py-1 text-[10px] text-[#525B6A] inline-flex items-center gap-1"><Download className="h-3 w-3" /> CSV</button>
                  <button className="border border-[#E2E8F0] rounded-md px-2 py-1 text-[10px] text-[#525B6A] inline-flex items-center gap-1"><Download className="h-3 w-3" /> XLSX</button>
                </div>
              </div>
              <div className="mt-3">
                <div className="text-[10px] font-semibold text-[#0A0D14] mb-1">CSV or XLSX file</div>
                <div className="flex items-center gap-2 border border-dashed border-[#E2E8F0] rounded-md px-3 py-2.5">
                  <Upload className="h-3.5 w-3.5 text-[#8A94A6]" />
                  <span className="text-[11px] text-[#8A94A6]">serpnex-import-migration.csv · 3.2 KB</span>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-end gap-2 pt-2">
                <button className="text-[11px] px-3 py-1.5 border border-[#E2E8F0] rounded-md text-[#525B6A]">Close</button>
                <button className="text-[11px] px-3 py-1.5 rounded-md bg-[#0047FF] text-white font-medium">Import file</button>
              </div>
            </div>

            {/* Overlay: Add Keywords mini-modal (top-right, offset) */}
            <div className="hidden lg:block absolute -top-8 -right-6 w-64 rotate-2 shadow-xl">
              <div className="card-crisp p-4 bg-white">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-display font-bold text-[#0A0D14]">Add keywords</div>
                  <X className="h-3.5 w-3.5 text-[#8A94A6]" />
                </div>
                <div className="mt-3 border-t border-[#E2E8F0] pt-3">
                  <div className="text-[10px] font-semibold text-[#0A0D14] mb-1">Paste keywords (one per line)</div>
                  <div className="border border-[#E2E8F0] rounded-md p-2 font-mono text-[10px] text-[#8A94A6] leading-5 min-h-[70px]">
                    best rank tracker<br />seo tools for agencies<br />keyword rank tracker
                  </div>
                  <div className="text-[9px] text-[#8A94A6] mt-1">3 keywords</div>
                </div>
                <button className="mt-3 w-full py-2 rounded-md bg-[#0047FF] text-white text-[11px] font-medium">Add &amp; track</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Opportunity Ranking */}
      <section className="border-t border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid md:grid-cols-2 gap-10 items-center">
          {/* Mock on the LEFT */}
          <div className="card-crisp p-6" data-testid="mock-opportunity-ranking">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#8A94A6] font-medium">Opportunity Ranking</div>
                <div className="mt-2 text-xl font-display font-bold text-[#0A0D14]">Work on these first.</div>
                <p className="mt-1 text-[11px] text-[#525B6A] leading-relaxed max-w-xs">Score combines search volume, position potential, and recent trend. Higher score = more traffic per unit of SEO effort.</p>
              </div>
              <div className="flex items-center justify-between gap-1.5 px-3 py-1.5 border-2 border-[#0A0D14] rounded-md text-xs text-[#0A0D14] shrink-0">
                SolarPanel.com <ChevronDown className="h-3.5 w-3.5 text-[#525B6A]" />
              </div>
            </div>
            {/* Min-score slider */}
            <div className="mt-4 border border-[#E2E8F0] rounded-md px-3 py-2.5 flex items-center gap-3">
              <span className="text-[10px] text-[#525B6A] font-medium shrink-0">Min score</span>
              <div className="relative flex-1 h-1 bg-[#E2E8F0] rounded-full">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-[#0047FF] border-2 border-white shadow-sm"></div>
              </div>
              <span className="text-xs tabular-nums text-[#0A0D14] font-medium">0</span>
            </div>
            {/* Table */}
            <div className="mt-3 border border-[#E2E8F0] rounded-md overflow-hidden">
              <div className="grid grid-cols-12 px-3 py-2 bg-[#F8F9FA] text-[9px] uppercase tracking-wider text-[#8A94A6] font-medium">
                <div className="col-span-1">#</div>
                <div className="col-span-4">Keyword</div>
                <div className="col-span-2">Rank</div>
                <div className="col-span-3">Score</div>
                <div className="col-span-2">Why</div>
              </div>
              {[
                { i: 1, kw: "top solar panel",   rank: "#8",  score: 50, why: "Needle-movable" },
                { i: 2, kw: "best solar panel",  rank: "#75", score: 13, why: "Needs SEO push" },
                { i: 3, kw: "solar panel US",    rank: "#76", score: 13, why: "Needs SEO push" },
                { i: 4, kw: "solar panel home",  rank: "#70", score: 13, why: "Needs SEO push" },
              ].map((r) => (
                <div key={r.i} className="grid grid-cols-12 items-center px-3 py-2.5 text-[11px] border-t border-[#E2E8F0]">
                  <div className="col-span-1 text-[#8A94A6] tabular-nums">{r.i}</div>
                  <div className="col-span-4">
                    <div className="text-[#0A0D14] font-medium truncate">{r.kw}</div>
                    <div className="text-[9px] text-[#8A94A6]">US · desktop · google</div>
                  </div>
                  <div className="col-span-2 tabular-nums text-[#0A0D14] font-medium">{r.rank}</div>
                  <div className="col-span-3">
                    <div className="flex items-center gap-1.5">
                      <div className="flex-1 h-1.5 bg-[#E2E8F0] rounded-full overflow-hidden">
                        <div className={`h-full ${r.score >= 40 ? 'bg-[#0047FF]' : 'bg-[#8A94A6]'}`} style={{ width: `${r.score}%` }} />
                      </div>
                      <span className="tabular-nums text-[#0A0D14] font-medium">{r.score}</span>
                    </div>
                  </div>
                  <div className="col-span-2 text-[10px] text-[#525B6A] truncate">{r.why}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-start gap-1.5 text-[10px] text-[#525B6A] leading-relaxed">
              <Info className="h-3 w-3 text-[#8A94A6] shrink-0 mt-0.5" />
              <span>Score is 0–100. Volume weight caps at 40 (log-scale). Position potential peaks between #4–#20.</span>
            </div>
          </div>

          {/* Description on the RIGHT */}
          <div>
            <div className="eyebrow">Opportunity Ranking</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold leading-snug text-[#0A0D14]">
              Know exactly which <br />keywords to work on first.
            </h2>
            <p className="mt-5 text-[#525B6A] leading-relaxed max-w-lg">
              Every keyword gets a 0–100 Opportunity Score that blends search volume, how close you already are to page 1, and recent momentum.
            </p>
            <p className="mt-3 text-[#525B6A] leading-relaxed max-w-lg">
              High scores surface the keywords where a little SEO effort will move the biggest amount of traffic — no more spreadsheets, no more guessing.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-[#0A0D14]">
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Combines volume, position potential &amp; recent trend into one number</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Prioritises the keywords sitting on the edge of the top 10</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Filter by minimum score to focus on high-value work only</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Plain-English reason next to every keyword</li>
            </ul>
            <Link to="/signup" data-testid="opportunity-ranking-cta" className="mt-8 inline-flex btn-ghost px-4 py-2.5 rounded-md text-sm items-center gap-2">See your opportunities <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* White-Label Reporting */}
      <section className="border-t border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid md:grid-cols-2 gap-10 items-center">
          {/* Description LEFT */}
          <div>
            <div className="eyebrow">White-Label Reporting</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold leading-snug text-[#0A0D14]">
              Client-ready PDF reports <br />with your own brand.
            </h2>
            <p className="mt-5 text-[#525B6A] leading-relaxed max-w-lg">
              Send a polished, agency-branded PDF to every client in one click.
            </p>
            <p className="mt-3 text-[#525B6A] leading-relaxed max-w-lg">
              Choose your own logo, agency name, accent colour, and footer disclaimer. Pick exactly which sections and keyword columns to include — SerpNex builds the report and hands it back to you instantly.
            </p>
            <p className="mt-3 text-[#525B6A] leading-relaxed max-w-lg">
              No email step, no watermark, no SerpNex mention unless you want it.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-[#0A0D14]">
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Use SerpNex logo, no logo, or upload your own PNG / SVG</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Set agency name, website, contact email &amp; brand accent colour</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Toggle any of the 7 sections and any of the 11 keyword columns</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> Custom date range or schedule daily / weekly / monthly / quarterly</li>
              <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> PDF opens instantly for download — no email hoops</li>
            </ul>
            <Link to="/signup" data-testid="white-label-cta" className="mt-8 inline-flex btn-ghost px-4 py-2.5 rounded-md text-sm items-center gap-2">See a sample report <ArrowRight className="h-4 w-4" /></Link>
          </div>

          {/* Mockup RIGHT — Generate White-Label Report modal (compact) */}
          <div className="card-crisp p-5 bg-white" data-testid="mock-white-label">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-lg font-display font-bold text-[#0A0D14]">Generate White-Label Report</div>
                <p className="mt-1 text-[11px] text-[#525B6A] leading-relaxed max-w-xs">Configure branding and sections. Produces a client-ready PDF with your logo, KPIs, movement tables, activity summary and recommendations.</p>
              </div>
              <X className="h-4 w-4 text-[#8A94A6] shrink-0" />
            </div>

            {/* Agency branding accordion */}
            <div className="mt-4 border border-[#E2E8F0] rounded-md p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-semibold text-[#0A0D14]">Agency branding</div>
                <ChevronDown className="h-3.5 w-3.5 text-[#8A94A6]" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] font-semibold text-[#0A0D14] mb-1">Agency name</div>
                  <div className="px-2.5 py-1.5 border border-[#E2E8F0] rounded-md text-[10px] text-[#8A94A6]">Northlane Agency</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-[#0A0D14] mb-1">Prepared by</div>
                  <div className="px-2.5 py-1.5 border border-[#E2E8F0] rounded-md text-[10px] text-[#8A94A6] truncate">Amy Chen, Head of SEO</div>
                </div>
              </div>
              <div className="mt-3">
                <div className="text-[10px] font-semibold text-[#0A0D14] mb-1">Header logo</div>
                <div className="grid grid-cols-3 gap-1.5">
                  <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border-2 border-[#0047FF] bg-[#0047FF]/[0.04] text-[10px]">
                    <span className="h-2.5 w-2.5 rounded-full border-2 border-[#0047FF] flex items-center justify-center"><span className="h-1 w-1 rounded-full bg-[#0047FF]"></span></span>
                    <span className="font-semibold text-[#0047FF]">Use SerpNex logo</span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-[#E2E8F0] text-[10px] text-[#525B6A]">
                    <span className="h-2.5 w-2.5 rounded-full border border-[#8A94A6]"></span> No logo
                  </div>
                  <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-[#E2E8F0] text-[10px] text-[#525B6A]">
                    <span className="h-2.5 w-2.5 rounded-full border border-[#8A94A6]"></span> Custom URL
                  </div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[10px] font-semibold text-[#0A0D14] mb-1">Website</div>
                  <div className="px-2.5 py-1.5 border border-[#E2E8F0] rounded-md text-[10px] text-[#8A94A6]">northlane.com</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-[#0A0D14] mb-1">Contact</div>
                  <div className="px-2.5 py-1.5 border border-[#E2E8F0] rounded-md text-[10px] text-[#8A94A6] truncate">hello@northlane.com</div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-[#0A0D14] mb-1">Accent</div>
                  <div className="px-2 py-1.5 border border-[#E2E8F0] rounded-md flex items-center gap-1.5 text-[10px] text-[#0A0D14]">
                    <span className="h-3 w-3 rounded-sm border border-[#E2E8F0]" style={{ background: "#29f500" }}></span>
                    <span className="font-mono">#29f500</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Report sections */}
            <div className="mt-3 border border-[#E2E8F0] rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-[#0A0D14]">Report sections <span className="text-[#8A94A6] font-normal">(7/7)</span></div>
                <ChevronDown className="h-3.5 w-3.5 text-[#8A94A6]" />
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  "Executive summary",
                  "Ranking distribution",
                  "Biggest wins",
                  "Biggest drops",
                  "High-value keywords",
                  "SEO activity timeline",
                ].map((s) => (
                  <div key={s} className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-[#0047FF]/25 bg-[#0047FF]/[0.04] text-[10px] text-[#0A0D14] font-medium">
                    <span className="h-3 w-3 rounded-sm bg-[#0047FF] text-white text-[9px] flex items-center justify-center shrink-0">✓</span>
                    <span className="truncate">{s}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Keyword table columns chip strip */}
            <div className="mt-3 border border-[#E2E8F0] rounded-md p-3">
              <div className="text-xs font-semibold text-[#0A0D14] mb-2">Keyword table columns</div>
              <div className="flex flex-wrap gap-1">
                {[
                  ["Keyword", true], ["Tags", false], ["URL", false], ["Engine", false], ["Country", true],
                  ["Device", true], ["Volume", true], ["Prev", true], ["Rank", true], ["Δ", true], ["Best", true], ["Opp.", true],
                ].map(([label, on]) => (
                  <span
                    key={label}
                    className={`px-2 py-0.5 rounded-md text-[9px] font-medium border ${on ? 'border-[#0047FF] text-[#0047FF] bg-[#0047FF]/[0.04]' : 'border-[#E2E8F0] text-[#8A94A6]'}`}
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 pt-3 border-t border-[#E2E8F0] flex items-center justify-between gap-2">
              <div className="text-[10px] text-[#8A94A6] max-w-[40%]">PDF downloads instantly — no email step.</div>
              <div className="flex items-center gap-2">
                <button className="text-[11px] px-3 py-1.5 border border-[#E2E8F0] rounded-md text-[#525B6A]">Cancel</button>
                <button className="text-[11px] px-3 py-1.5 rounded-md bg-[#0047FF] text-white font-medium inline-flex items-center gap-1.5"><Download className="h-3 w-3" /> Generate PDF</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="max-w-2xl">
          <div className="eyebrow">What makes SerpNex different</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold text-[#0A0D14]">Simple Rank Tracking for SEO Professionals</h2>
          <p className="mt-4 text-[#525B6A]">Six focused capabilities that work whether you&apos;re tracking 5 keywords or 5,000. No backlink dashboard, no AI writer, no bloat.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div key={i} className="card-crisp p-6" data-testid={`home-feature-${i}`}>
              <div className="h-9 w-9 rounded-md bg-[#E0E7FF] text-[#0047FF] flex items-center justify-center mb-4">
                <f.icon className="h-4.5 w-4.5" />
              </div>
              <div className="text-lg font-display font-semibold text-[#0A0D14]">{f.title}</div>
              <div className="mt-2 text-sm text-[#525B6A] leading-relaxed">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#0A0D14] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <div className="eyebrow" style={{ color: '#7EA1FF' }}>How it works</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-display font-bold">Easy Setup: Import, Bulk Add or Manual</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Create a project", b: "Enter your domain or your client's. Pick a default country, language, device." },
              { n: "02", t: "Paste keywords", b: "One per line. Or drop a CSV with search volume. Bulk-add hundreds at once." },
              { n: "03", t: "Get answers", b: "See rankings, top movers, and priority opportunities — every morning." },
            ].map((s, i) => (
              <div key={i} className="border border-white/10 rounded-lg p-6">
                <div className="font-mono text-xs text-[#7EA1FF]">{s.n}</div>
                <div className="mt-3 text-xl font-display font-semibold">{s.t}</div>
                <div className="mt-2 text-sm text-white/70 leading-relaxed">{s.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solo + Agency split */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card-crisp p-8" data-testid="pitch-solo">
            <div className="eyebrow">For solo users</div>
            <h3 className="mt-3 text-2xl font-display font-bold text-[#0A0D14]">Try free for 7 days. Track keywords in a minute.</h3>
            <p className="mt-3 text-sm text-[#525B6A] leading-relaxed">Bloggers, freelancers, consultants, and small business owners. No card. No sales call. Just paste your keywords and watch your rankings update daily.</p>
            <Link to="/signup" data-testid="pitch-solo-cta" className="mt-6 inline-flex btn-primary px-4 py-2.5 rounded-md text-sm items-center gap-2">Start 7-Day Free Trial <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="card-crisp p-8" data-testid="pitch-agency">
            <div className="eyebrow">For agencies &amp; teams</div>
            <h3 className="mt-3 text-2xl font-display font-bold text-[#0A0D14]">White-label reports on your domain.</h3>
            <p className="mt-3 text-sm text-[#525B6A] leading-relaxed">Send branded PDF reports and share links from <strong>reports.your-agency.com</strong>. Team seats, custom intro copy, configurable sections. Clients never see SerpNex.</p>
            <Link to="/pricing" data-testid="pitch-agency-cta" className="mt-6 inline-flex btn-ghost px-4 py-2.5 rounded-md text-sm items-center gap-2">See pricing <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="stat-value text-3xl font-bold text-[#0A0D14]">{value}</div>
      <div className="text-xs uppercase tracking-wider text-[#8A94A6] mt-1">{label}</div>
    </div>
  );
}

function MockDashboardCard() {
  const rows = [
    { kw: "best keyword tracker", c: 3, p: 6, vol: 2400 },
    { kw: "seo for small business", c: 8, p: 12, vol: 9600 },
    { kw: "serp rank checker", c: 14, p: 11, vol: 4400, down: true },
    { kw: "google ranking tool", c: 2, p: 4, vol: 1800 },
  ];
  return (
    <div className="card-crisp p-5 relative">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[13px] text-[#525B6A]">demo.serpnex.com</div>
          <div className="text-xs text-[#8A94A6]">Updated 2 min ago · US · Desktop</div>
        </div>
        <span className="pill pill-success"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Fresh</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="border border-[#E2E8F0] rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Improved</div>
          <div className="stat-value text-xl font-bold text-emerald-600">+12</div>
        </div>
        <div className="border border-[#E2E8F0] rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Top 10</div>
          <div className="stat-value text-xl font-bold text-[#0047FF]">28</div>
        </div>
        <div className="border border-[#E2E8F0] rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Opportunity</div>
          <div className="stat-value text-xl font-bold text-[#0A0D14]">47</div>
        </div>
      </div>
      <div className="border border-[#E2E8F0] rounded-md overflow-hidden">
        <div className="grid grid-cols-12 px-3 py-2 bg-[#F8F9FA] text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium">
          <div className="col-span-6">Keyword</div>
          <div className="col-span-3 text-right">Rank</div>
          <div className="col-span-3 text-right">Change</div>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 items-center px-3 py-2 text-xs border-t border-[#E2E8F0]">
            <div className="col-span-6 text-[#0A0D14] truncate">{r.kw}</div>
            <div className="col-span-3 text-right tabular-nums font-medium">#{r.c}</div>
            <div className={`col-span-3 text-right tabular-nums font-medium inline-flex items-center justify-end gap-0.5 ${r.down ? 'text-red-600' : 'text-emerald-600'}`}>
              {r.down ? <ArrowDownRight className="h-3 w-3" /> : <ArrowUpRight className="h-3 w-3" />}{Math.abs(r.p - r.c)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CTASection() {
  return (
    <section className="border-t border-[#E2E8F0]">
      <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-3xl sm:text-5xl font-display font-bold text-[#0A0D14]">Start Tracking Your Keywords.</h2>
        <p className="mt-4 text-[#525B6A] max-w-xl mx-auto">Try any plan free for 7 days. No credit card required. Cancel with one click.</p>
        <div className="mt-8 flex justify-center gap-3 flex-wrap">
          <Link to="/signup" data-testid="footer-cta-signup" className="btn-primary px-6 py-3 rounded-md text-sm inline-flex items-center gap-2">Start 7-Day Free Trial <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/pricing" data-testid="footer-cta-pricing" className="btn-ghost px-6 py-3 rounded-md text-sm">See pricing</Link>
        </div>
      </div>
    </section>
  );
}
